// Performance and Risk Analysis
export class PerformanceAnalysis {
    /**
     * Calculate maximum drawdown
     */
    static calculateMaxDrawdown(prices: number[]): number {
        let maxPrice = prices[0];
        let maxDrawdown = 0;

        for (let i = 1; i < prices.length; i++) {
            if (prices[i] > maxPrice) {
                maxPrice = prices[i];
            }
            const drawdown = (maxPrice - prices[i]) / maxPrice;
            if (drawdown > maxDrawdown) {
                maxDrawdown = drawdown;
            }
        }

        return maxDrawdown;
    }

    /**
     * Calculate maximum consecutive losses
     */
    static calculateMaxConsecutiveLosses(prices: number[]): number {
        let maxConsecutive = 0;
        let currentConsecutive = 0;

        for (let i = 1; i < prices.length; i++) {
            if (prices[i] < prices[i - 1]) {
                currentConsecutive++;
                maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
            } else {
                currentConsecutive = 0;
            }
        }

        return maxConsecutive;
    }

    /**
     * Calculate win rate
     */
    static calculateWinRate(prices: number[]): number {
        let wins = 0;
        let total = 0;

        for (let i = 1; i < prices.length; i++) {
            if (prices[i] !== prices[i - 1]) {
                total++;
                if (prices[i] > prices[i - 1]) {
                    wins++;
                }
            }
        }

        return total > 0 ? wins / total : 0;
    }

    /**
     * Calculate profit factor
     */
    static calculateProfitFactor(prices: number[]): number {
        let grossProfit = 0;
        let grossLoss = 0;

        for (let i = 1; i < prices.length; i++) {
            const change = prices[i] - prices[i - 1];
            if (change > 0) {
                grossProfit += change;
            } else {
                grossLoss += Math.abs(change);
            }
        }

        return grossLoss > 0 ? grossProfit / grossLoss : 0;
    }

    /**
     * Calculate Sharpe Ratio
     */
    static calculateSharpeRatio(returns: number[], riskFreeRate = 0.02): number {
        if (returns.length === 0) return 0;

        const meanReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
        const excessReturn = meanReturn - riskFreeRate;
        const variance = returns.reduce((sum, ret) => sum + (ret - meanReturn) ** 2, 0) / returns.length;
        const volatility = Math.sqrt(variance);

        return volatility > 0 ? excessReturn / volatility : 0;
    }

    /**
     * Calculate Sortino Ratio
     */
    static calculateSortinoRatio(returns: number[], riskFreeRate = 0.02): number {
        if (returns.length === 0) return 0;

        const meanReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
        const excessReturn = meanReturn - riskFreeRate;

        // Calculate downside deviation (only negative returns)
        const negativeReturns = returns.filter((ret) => ret < meanReturn);
        const downsideVariance =
            negativeReturns.reduce((sum, ret) => sum + (ret - meanReturn) ** 2, 0) / returns.length;
        const downsideDeviation = Math.sqrt(downsideVariance);

        return downsideDeviation > 0 ? excessReturn / downsideDeviation : 0;
    }

    /**
     * Calculate Calmar Ratio
     */
    static calculateCalmarRatio(returns: number[], maxDrawdown: number): number {
        if (maxDrawdown === 0) return 0;

        const meanReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
        return meanReturn / maxDrawdown;
    }

    /**
     * Calculate Position Size
     */
    static calculatePositionSize(targetEntry: number, stopLoss: number): number {
        const riskPerShare = Math.abs(targetEntry - stopLoss);
        return riskPerShare > 0 ? 100 / riskPerShare : 1;
    }

    /**
     * Calculate Confidence
     */
    static calculateConfidence(signals: string[]): number {
        return Math.min(signals.length * 10, 100);
    }

    /**
     * Calculate Risk Level
     */
    static calculateRiskLevel(volatility: number): string {
        if (volatility < 20) return 'LOW';
        if (volatility < 40) return 'MEDIUM';
        return 'HIGH';
    }
}
